// Employee-facing privacy summary — Employee Portal & App spec §14.
//
// A plain-English description of what HR and managers can and cannot see, so an
// employee doesn't have to read a long policy to understand the basics (§14).
// This is DESCRIPTIVE — it states what the platform actually does (the §14.2 /
// §14.3 tables), not a new commitment. The single load-bearing invariant — a
// manager never sees private Maeve conversation content — is the SAME boundary
// the §11.2 visibility matrix enforces for `maeveContent` (proven in the
// visibility-wiring tests); `privacySummaryMatchesMatrix` ties them together so
// the copy can't drift from the enforced behaviour.
//
// NOTE: the framing sentences are the spec's §14.1 suggested wording. Like the
// AI-disclosure copy they make privacy statements, so the wording is subject to
// legal-counsel review before pilot (Track B) — but unlike the disclosure they
// are not gated by a system_prompt loader, so they live here for review.

import { preferenceVisibleTo } from './preference-visibility.ts';

// §14.1 — the plain-English summary shown before/alongside Maeve and the portal.
export const PRIVACY_SUMMARY_PARAGRAPHS: readonly string[] = [
  'Your manager and HR can see the practical information they need to support you, such as your contact preferences, your support pathway and whether key workplace actions are happening.',
  'Your private conversations with Maeve are not visible to your manager.',
  'If Maeve identifies a serious safety concern, besideU may alert the named HR contact so they can follow your organisation’s escalation process.',
];

export type PrivacyAccess = 'visible' | 'not_visible' | 'conditional';

export interface PrivacyVisibilityRow {
  // What the data is, in the employee's words.
  data: string;
  access: PrivacyAccess;
  // For `conditional` rows, the qualifying detail (e.g. "anonymised unless …").
  // Also used to add nuance to a plain visible/not-visible row.
  note?: string;
}

// §14.2 — what HR can see.
export const HR_VISIBILITY: readonly PrivacyVisibilityRow[] = [
  { data: 'Your name', access: 'visible' },
  { data: 'Your support pathway', access: 'visible' },
  { data: 'Touch point status', access: 'visible' },
  { data: 'Your contact preferences', access: 'visible' },
  { data: 'Leave and entitlement information', access: 'visible' },
  { data: 'Whether you have opened the portal', access: 'visible', note: 'Yes / no only' },
  { data: 'Your private Maeve conversations', access: 'not_visible' },
  {
    data: 'Amber wellbeing signals',
    access: 'conditional',
    note: 'Anonymised (no name) unless your organisation’s policy requires otherwise',
  },
  {
    data: 'Red (serious safety) alerts',
    access: 'conditional',
    note: 'The named HR contact is notified so they can follow the escalation process',
  },
  {
    data: 'Resources you have viewed',
    access: 'not_visible',
    note: 'Not surfaced unless there is a clear need',
  },
];

// §14.3 — what your manager can see.
export const MANAGER_VISIBILITY: readonly PrivacyVisibilityRow[] = [
  { data: 'Your name', access: 'visible' },
  { data: 'Your support pathway', access: 'visible' },
  { data: 'Touch point guidance', access: 'visible' },
  { data: 'Your contact preferences', access: 'visible' },
  {
    data: 'Leave and return-to-work information relevant to their role',
    access: 'visible',
  },
  { data: 'Your private Maeve conversations', access: 'not_visible' },
  { data: 'Resources you have viewed', access: 'not_visible' },
  { data: 'Private notes or reflections', access: 'not_visible' },
  { data: 'Amber wellbeing detail', access: 'not_visible' },
  {
    data: 'Red (serious safety) detail',
    access: 'conditional',
    note: 'Only if your organisation’s escalation process requires it',
  },
];

// Ties the static copy to the enforced matrix: the "manager/HR cannot see your
// private Maeve conversations" rows must agree with `maeveContent === 'none'`.
// Unit-tested so a matrix change that ever loosened this would fail loudly.
export function privacySummaryMatchesMatrix(): boolean {
  const hrMaeve = HR_VISIBILITY.find((r) => r.data === 'Your private Maeve conversations');
  const mgrMaeve = MANAGER_VISIBILITY.find((r) => r.data === 'Your private Maeve conversations');
  return (
    hrMaeve?.access === 'not_visible' &&
    mgrMaeve?.access === 'not_visible' &&
    preferenceVisibleTo('HR', 'maeveContent') === 'none' &&
    preferenceVisibleTo('Manager', 'maeveContent') === 'none'
  );
}
